import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyAPI, jobsAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import AnimatedTagline from '../components/AnimatedTagline';
import { useTheme } from '../context/ThemeContext';
import { MEDIA_URL } from '../config';
import '../styles/theme.css';
import './CompanyProfile.css';

function CompanyProfile() {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const decodedName = decodeURIComponent(companyName);

  useEffect(() => { loadAll(); }, [companyName]);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const profileRes = await companyAPI.getByCompany(decodedName);
      const data = profileRes.data;
      if (data.company_logo && !data.company_logo.startsWith('http')) {
        data.company_logo = `${MEDIA_URL}/${data.company_logo.replace(/^\/+/, '')}`;
      }
      setProfile(data);
      if (data.user_id) {
        try {
          const jobsRes = await jobsAPI.getJobsByRecruiter(data.user_id);
          setJobs(jobsRes.data || []);
        } catch {}
      }
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else setError(err.message || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const map = { 'Full-time': 'badge-blue', 'Part-time': 'badge-orange', 'Contract': 'badge-purple', 'Internship': 'badge-green' };
    return map[type] || 'badge-gray';
  };

  if (loading) return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state">
            <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
            <p>Loading company profile...</p>
          </div>
        </div>
      </main>
    </div>
  );

  if (notFound || error) return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state">
            <i className="fas fa-building"></i>
            <h3>{notFound ? 'Company not found' : 'Failed to load'}</h3>
            <p>{notFound ? "This company hasn't set up a profile yet." : error}</p>
            <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate(-1)}>Go Back</button>
          </div>
        </div>
      </main>
    </div>
  );

  if (!profile) return null;

  const coverStyle = profile.cover_image ? {
    backgroundImage: `url(${profile.cover_image.startsWith('http') ? profile.cover_image : `${MEDIA_URL}/${profile.cover_image.replace(/^\/+/, '')}`})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  } : {};

  const hasSocial = profile.linkedin_url || profile.facebook_url || profile.twitter_url || profile.instagram_url;

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <button className="topbar-btn" onClick={() => navigate(-1)} title="Go back">
            <i className="fas fa-arrow-left"></i>
          </button>
          <span className="topbar-title">{profile.company_name}</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">

          {/* ── Hero banner ── */}
          <div className="cp-hero card" style={coverStyle}>
            <div className="cp-hero-bg" style={profile.cover_image ? { opacity: 0.6 } : {}}>
              <div className="cp-blob cp-blob-1" style={profile.brand_color ? { background: profile.brand_color } : {}} />
              <div className="cp-blob cp-blob-2" style={profile.brand_color ? { background: profile.brand_color } : {}} />
              <div className="cp-blob cp-blob-3" />
            </div>
            <div className="cp-hero-logo">
              {profile.company_logo
                ? <img src={profile.company_logo} alt={profile.company_name} className="cp-logo-img" />
                : <div className="cp-logo-placeholder">{profile.company_name?.charAt(0)}</div>
              }
            </div>
            <div className="cp-hero-info">
              <h1 className="cp-company-name"><span className="cp-animated-name">{profile.company_name}</span></h1>
              {profile.tagline && profile.animate_tagline ? (
                <p className="cp-industry" style={{ margin: 0 }}>
                  <AnimatedTagline tagline={profile.tagline} wordClassName="cp-tagline-word" />
                </p>
              ) : (
                <p className="cp-industry">{profile.tagline || profile.industry || ''}</p>
              )}
              <div className="cp-hero-badges">
                {profile.is_verified && (
                  <span className="badge badge-green"><i className="fas fa-check-circle"></i> Verified Employer</span>
                )}
                {profile.company_size && (
                  <span className="badge badge-blue"><i className="fas fa-users"></i> {profile.company_size} employees</span>
                )}
                {profile.founded_year && (
                  <span className="badge badge-gray"><i className="fas fa-calendar-alt"></i> Est. {profile.founded_year}</span>
                )}
                {profile.city && (
                  <span className="badge badge-gray">
                    <i className="fas fa-map-marker-alt"></i> {profile.city}{profile.province ? `, ${profile.province}` : ''}
                  </span>
                )}
              </div>
              {profile.company_website && (
                <a href={profile.company_website} target="_blank" rel="noopener noreferrer" className="cp-website-link">
                  <i className="fas fa-globe"></i> {profile.company_website}
                </a>
              )}
            </div>
          </div>

          <div className="cp-body">

            {/* ── Left / main column ── */}
            <div className="cp-main">

              {/* About */}
              {profile.about_company && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-info-circle" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>About {profile.company_name}</span>
                  </div>
                  <p className="cp-text">{profile.about_company}</p>
                </div>
              )}

              {/* Mission */}
              {profile.mission_statement && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-bullseye" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Mission</span>
                  </div>
                  <p className="cp-text cp-mission">{profile.mission_statement}</p>
                </div>
              )}

              {/* Culture */}
              {profile.company_culture && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-heart" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Culture</span>
                  </div>
                  <p className="cp-text">{profile.company_culture}</p>
                </div>
              )}

              {/* Values */}
              {profile.values && profile.values.length > 0 && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-star" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Values</span>
                  </div>
                  <div className="cp-benefits">
                    {profile.values.map((v, i) => (
                      <span key={i} className="cp-benefit-tag" style={{ background: 'var(--primary-light, rgba(99,102,241,0.12))', color: 'var(--primary)', border: '1px solid var(--primary-border, rgba(99,102,241,0.25))' }}>
                        <i className="fas fa-check"></i> {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Interview Process */}
              {profile.interview_process && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-comments" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Interview Process</span>
                  </div>
                  <p className="cp-text">{profile.interview_process}</p>
                  {profile.typical_hiring_timeline && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className="fas fa-clock" style={{ color: 'var(--text-muted)' }}></i>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Typical timeline: <strong>{profile.typical_hiring_timeline}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Industries hiring for */}
              {profile.industries_hiring_for && profile.industries_hiring_for.length > 0 && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-briefcase" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Industries We Hire For</span>
                  </div>
                  <div className="cp-benefits">
                    {profile.industries_hiring_for.map((ind, i) => (
                      <span key={i} className="cp-benefit-tag">
                        <i className="fas fa-industry"></i> {ind}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Open Positions */}
              <div className="card cp-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-briefcase" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Open Positions ({jobs.length})</span>
                </div>
                {jobs.length === 0 ? (
                  <div className="empty-state" style={{ padding: '2rem 0' }}>
                    <i className="fas fa-briefcase"></i>
                    <p>No open positions at the moment</p>
                  </div>
                ) : (
                  <div className="cp-jobs-list">
                    {jobs.map(job => (
                      <div key={job.id} className="cp-job-row" onClick={() => navigate(`/jobs/${job.id}`)}>
                        <div className="cp-job-info">
                          <span className="cp-job-title">{job.title}</span>
                          <div className="cp-job-meta">
                            {job.city && <span><i className="fas fa-map-marker-alt"></i> {job.city}</span>}
                            {job.work_mode && <span><i className="fas fa-laptop-house"></i> {job.work_mode}</span>}
                            {(job.salary_min || job.salary_max) && (
                              <span><i className="fas fa-money-bill-wave"></i> R{job.salary_min?.toLocaleString()} – R{job.salary_max?.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="cp-job-right">
                          <span className={`badge ${getTypeBadge(job.employment_type)}`}>{job.employment_type || 'Full-time'}</span>
                          <i className="fas fa-chevron-right cp-job-arrow"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right sidebar ── */}
            <div className="cp-sidebar">

              {/* Benefits */}
              {profile.benefits && profile.benefits.length > 0 && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-gift" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Benefits</span>
                  </div>
                  <div className="cp-benefits">
                    {profile.benefits.map((b, i) => (
                      <span key={i} className="cp-benefit-tag">
                        <i className="fas fa-check"></i> {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Company details */}
              <div className="card cp-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-building" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Company Details</span>
                </div>
                {profile.industry && (
                  <div className="cp-contact-row">
                    <i className="fas fa-industry"></i>
                    <span>{profile.industry}</span>
                  </div>
                )}
                {profile.company_size && (
                  <div className="cp-contact-row">
                    <i className="fas fa-users"></i>
                    <span>{profile.company_size} employees</span>
                  </div>
                )}
                {profile.founded_year && (
                  <div className="cp-contact-row">
                    <i className="fas fa-calendar-alt"></i>
                    <span>Founded {profile.founded_year}</span>
                  </div>
                )}
                {profile.typical_hiring_timeline && !profile.interview_process && (
                  <div className="cp-contact-row">
                    <i className="fas fa-clock"></i>
                    <span>Hires in {profile.typical_hiring_timeline}</span>
                  </div>
                )}
              </div>

              {/* Contact */}
              {(profile.contact_person_name || profile.phone || profile.city) && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-address-card" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Contact</span>
                  </div>
                  {profile.contact_person_name && (
                    <div className="cp-contact-row">
                      <i className="fas fa-user"></i>
                      <span>{profile.contact_person_name}{profile.contact_person_title ? ` · ${profile.contact_person_title}` : ''}</span>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="cp-contact-row">
                      <i className="fas fa-phone"></i>
                      <span>{profile.phone}</span>
                    </div>
                  )}
                  {profile.city && (
                    <div className="cp-contact-row">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{[profile.street_address, profile.city, profile.province, profile.postal_code].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {profile.company_website && (
                    <div className="cp-contact-row">
                      <i className="fas fa-globe"></i>
                      <a href={profile.company_website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                        {profile.company_website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Social links */}
              {hasSocial && (
                <div className="card cp-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-share-alt" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Follow Us</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
                    {profile.linkedin_url && (
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#0a66c2', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600 }}>
                        <i className="fab fa-linkedin" style={{ fontSize: '1.2rem' }}></i> LinkedIn
                      </a>
                    )}
                    {profile.facebook_url && (
                      <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#1877f2', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600 }}>
                        <i className="fab fa-facebook" style={{ fontSize: '1.2rem' }}></i> Facebook
                      </a>
                    )}
                    {profile.twitter_url && (
                      <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-primary)', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600 }}>
                        <i className="fab fa-x-twitter" style={{ fontSize: '1.2rem' }}></i> X
                      </a>
                    )}
                    {profile.instagram_url && (
                      <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#e1306c', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600 }}>
                        <i className="fab fa-instagram" style={{ fontSize: '1.2rem' }}></i> Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CompanyProfile;
